/**
 * Escudo777 - Seguro de Empate (draw insurance) eligibility helpers.
 *
 * Everything is driven by the CMS payload exposed under `single` by
 * GET /api/cms/promotions/insurances, so scope changes made by the operator
 * are reflected without a deployment (on the next page load).
 *
 * Multi-campaign: `data.single` carries one shared promotion (id, page,
 * opt-in) plus an array of `campaigns`, each with its own event scope,
 * settings and prize. There is no longer a single "current campaign" -
 * every lookup below resolves the campaign that covers a specific event,
 * selection or bet.
 */

const MATCH_RESULT_MARKETS = ['Match Result', 'Match Result (Regular Time)']
const DRAW_OUTCOME_LABELS = ['empate', 'draw', 'x', 'tie']

export default {
  computed: {
    seguro_empate_promotion_id() {
      return this.$store.getters.seguroEmpatePromotionId
    },
    seguro_empate_opted_in() {
      return this.$store.getters.seguroEmpateOptedIn
    },
    /**
     * Events already insured this campaign, flattened across every running
     * campaign - the one-per-event rule applies regardless of which campaign
     * an event sits under.
     */
    seguro_empate_insured_event_ids() {
      return this.$store.getters.seguroEmpateInsuredEventIds
    }
  },
  methods: {
    /**
     * The running campaign whose scope covers this event, or null. Two
     * campaigns can be active with different event lists at once, so this
     * checks each campaign's scopes in turn rather than one flattened list.
     */
    seguroEmpateCampaignFor(event) {
      if (!event) return null
      const sportSlug = _.get(event, 'sport_slug') || _.get(event, 'sport.slug')
      const sportId = _.get(event, 'sport_id') || _.get(event, 'sport.id')
      const competitionId = _.get(event, 'competition_id') || _.get(event, 'competition.id')
      const eventId = this.seguroEmpateEventId(event)

      return this.$store.getters.seguroEmpateRunningCampaigns.find((campaign) => {
        const sportConfigs = _.get(campaign, 'settings.sport_configs', [])
        const scopes = _.flatMap(sportConfigs, (sc) => sc.config || [])
        return scopes.some((scope) => {
          // A scope entry always names a sport in the CMS. Bail out on an
          // unscoped entry rather than badging every sport (basketball, tennis...).
          if (!scope.sport_id && !scope.provider_sport_id) return false

          if (scope.sport_id && String(scope.sport_id).toLowerCase() !== String(sportSlug).toLowerCase()) return false
          if (scope.provider_sport_id && parseInt(scope.provider_sport_id) !== parseInt(sportId)) return false
          if (scope.provider_competition_id && parseInt(scope.provider_competition_id) !== parseInt(competitionId)) return false
          if (scope.provider_event_id && parseInt(scope.provider_event_id) !== eventId) return false
          return true
        })
      }) || null
    },
    /**
     * The campaign that actually insured a settled bet, found via
     * `insured_bet_ids` rather than re-deriving scope - settled tickets don't
     * carry the full event object (sport/competition ids) scope-matching needs.
     */
    seguroEmpateCampaignForBet(betId) {
      return this.$store.getters.seguroEmpateCampaignForBetId(betId)
    },
    seguroEmpateConfigFor(event) {
      return _.get(this.seguroEmpateCampaignFor(event), 'settings') || null
    },
    seguroEmpatePrizeFor(event) {
      return _.get(this.seguroEmpateCampaignFor(event), 'prizes[0]') || null
    },
    seguroEmpateMinOddsFor(event) {
      const raw = _.get(this.seguroEmpateConfigFor(event), 'minimum_odds_per_selection')
      return raw ? parseFloat(raw) : 0
    },
    seguroEmpateMinStakeFor(event) {
      const raw = _.get(this.seguroEmpateConfigFor(event), 'minimum_bet_amount')
      return raw ? parseFloat(raw) : 0
    },

    /**
     * The conditions sentence split into plain and accented runs, so the
     * percentage and cap can be highlighted without rendering translated copy
     * as HTML. Shared by the betslip toggle and the bet confirmation panel so
     * both state the offer identically.
     */
    seguroEmpateConditionSegmentsForCampaign(campaign) {
      const prize = _.get(campaign, 'prizes[0]') || null
      const percentage = _.get(prize, 'price_type') === 'percent' ? parseFloat(_.get(prize, 'value', 0)) || null : null
      const cap = parseFloat(_.get(prize, 'max_value', 0)) || null

      let template
      const values = {}
      if (percentage && cap) {
        template = this.$t('DRAW_INSURANCE_CONDITIONS_PERCENT_CAPPED')
        values['{percentage}'] = `${percentage}%`
        values['{cap}'] = this.format_money(cap * 100)
      } else if (cap) {
        template = this.$t('DRAW_INSURANCE_CONDITIONS_CAPPED')
        values['{cap}'] = this.format_money(cap * 100)
      } else {
        template = this.$t('DRAW_INSURANCE_CONDITIONS_GENERIC')
      }

      return template
          .split(/(\{percentage\}|\{cap\})/g)
          .filter((part) => part !== '')
          .map((part) => ({
            text: values[part] !== undefined ? values[part] : part,
            accent: values[part] !== undefined
          }))
    },
    seguroEmpateConditionSegmentsFor(event) {
      return this.seguroEmpateConditionSegmentsForCampaign(this.seguroEmpateCampaignFor(event))
    },

    seguroEmpateEligible(event) {
      if (!event) return false
      if (!this.seguroEmpateIsPreMatch(event)) return false
      const campaign = this.seguroEmpateCampaignFor(event)
      if (!campaign) return false
      // MVP ships pre-match only. `event_type` is 'pre-match' | 'live' | 'both'.
      return _.get(campaign, 'settings.event_type') !== 'live'
    },
    /**
     * The badge disappears as soon as the match goes in-play or has finished.
     */
    seguroEmpateIsPreMatch(event) {
      if (_.get(event, 'is_live')) return false
      if (_.get(event, 'status') === 'live') return false
      if (_.includes(['completed', 'finished', 'closed'], _.get(event, 'live_status'))) return false

      const startsAt = _.get(event, 'starts_at')
      if (startsAt && this.$date(startsAt).isBefore()) return false

      return true
    },
    /**
     * Listing/detail feeds expose the prefixed id ('1-30039238'), the CMS
     * stores the bare provider id.
     */
    seguroEmpateEventId(event) {
      const id = _.get(event, 'id')
      if (!id) return null
      return parseInt(this.getEventId(String(id)))
    },

    /**
     * Can this betslip selection actually be insured? Scope, plus the rules
     * that disqualify it: backing the draw, and odds under the minimum.
     */
    seguroEmpateSelectionEligible(selection) {
      if (!this.seguroEmpateSelectionInScope(selection)) return false
      if (this.seguroEmpateIsDrawOutcome(selection)) return false
      if (this.seguroEmpateOddsBelowMinimum(selection)) return false
      return true
    },

    /**
     * Is this selection covered by the promotion at all? Event scope and
     * market only.
     *
     * Kept separate from eligibility so the betslip can still show the offer
     * and explain why it does not apply (draw selected, odds too low, stake
     * too small) instead of silently hiding it.
     */
    seguroEmpateSelectionInScope(selection) {
      if (!selection) return false
      if (!this.seguro_empate_opted_in) return false
      if (!this.seguroEmpateEligible(_.get(selection, 'event'))) return false
      if (!this.seguroEmpateMarketEligible(selection)) return false
      return true
    },

    seguroEmpateOddsBelowMinimum(selection) {
      const minOdds = this.seguroEmpateMinOddsFor(_.get(selection, 'event'))
      if (!minOdds) return false
      const odds = parseFloat(_.get(selection, 'outcome.odds', 0))
      if (!odds) return false
      return odds < minOdds
    },

    /**
     * The promotion covers 1X2 Match Result.
     *
     * The CMS fixes the market when an event is added to the bet conditions,
     * so the scope's `only_markets`/`exclude_markets` can only ever say the
     * same thing this does - reading them added no coverage, only ways to
     * disagree.
     */
    seguroEmpateMarketEligible(selection) {
      const marketName =
          _.get(selection, 'market.name_untranslated') || _.get(selection, 'market.name')
      return MATCH_RESULT_MARKETS.includes(marketName)
    },

    /**
     * Draw insurance covers Home or Away only - backing the draw itself
     * cannot be insured against a draw.
     */
    seguroEmpateIsDrawOutcome(selection) {
      const name = String(_.get(selection, 'outcome.name', '')).trim().toLowerCase()
      return DRAW_OUTCOME_LABELS.includes(name)
    },

    /**
     * Effective stake for one selection. Same rule the betslip already uses
     * for its own totals (calc.vue): the per-selection stake, falling back to
     * the shared single stake when the line has no input of its own.
     */
    seguroEmpateSelectionStake(selection) {
      const key = _.get(selection, 'outcome.id')
      const betslip = this.$store.state.betslip
      const stake = _.get(betslip, 'stakes.' + key, _.get(betslip, 'stakeSingle'))
      return parseFloat(stake) || 0
    },

    seguroEmpateInsured(selection) {
      return !!_.get(this.$store.state.betslip.insured, _.get(selection, 'outcome.id'))
    },

    /**
     * Is the insurance actually active on this selection right now? The
     * single source of truth for every Escudo777 marker in the betslip - the
     * switch, the shield on the line and the chip all read this, so they
     * cannot disagree.
     */
    seguroEmpateActive(selection, freebet = null) {
      if (freebet) return false
      if (_.get(this.$store.state.betslip, 'betType') !== 'single') return false
      if (!this.seguroEmpateInsured(selection)) return false
      if (!this.seguroEmpateSelectionQualifies(selection)) return false
      return !this.seguroEmpateEventAlreadyInsured(selection)
    },

    /**
     * Would this selection actually be insured if placed now? Eligibility
     * plus the minimum stake.
     *
     * Deliberately excludes the one-per-event rule so it can be used to
     * evaluate other selections without recursing.
     */
    seguroEmpateSelectionQualifies(selection) {
      if (!this.seguroEmpateSelectionEligible(selection)) return false
      if (!this.seguroEmpateCampaignFor(_.get(selection, 'event'))) return false
      const minStake = this.seguroEmpateMinStakeFor(_.get(selection, 'event'))
      if (minStake && this.seguroEmpateSelectionStake(selection) < minStake) return false
      return true
    },

    /**
     * The promotion allows one insured bet per event, but two singles on the
     * same match can sit in the betslip together (calc.vue only switches to
     * combi when event ids are unique), so both would otherwise offer
     * insurance.
     *
     * Whichever line the customer actually opted in holds the match; every
     * other line on it is blocked, regardless of position. Two lines can
     * never block each other because seguroEmpateSetInsured() keeps at most
     * one opt-in per event.
     */
    seguroEmpateEventAlreadyInsured(selection) {
      const eventId = _.get(selection, 'event.id')
      const outcomeId = _.get(selection, 'outcome.id')
      if (!eventId) return false

      // Already insured in an earlier bet this campaign - the API reports
      // these directly on each campaign's `insured_event_ids`.
      const providerEventId = this.seguroEmpateEventId(_.get(selection, 'event'))
      if (providerEventId && _.includes(this.seguro_empate_insured_event_ids, providerEventId)) {
        return true
      }

      const insuredMap = _.get(this.$store.state.betslip, 'insured', {})
      return _.some(_.get(this.$store.state.betslip, 'selections', []), (other) => {
        if (_.get(other, 'outcome.id') === outcomeId) return false
        if (_.get(other, 'event.id') !== eventId) return false
        if (!_.get(insuredMap, _.get(other, 'outcome.id'))) return false
        // A line that no longer qualifies releases the match, so the customer
        // is not stuck behind a bet that would not be insured anyway.
        return this.seguroEmpateSelectionQualifies(other)
      })
    },

    /**
     * Opting in also releases any other line on the same match, so at most
     * one opt-in per event is ever stored. That keeps the stored state
     * identical to what is rendered and lets the one-per-event check stay
     * symmetric without two lines deadlocking each other.
     *
     * In practice the other line is already disabled, so this is a safety net
     * for states reached another way (a price recovering, a selection
     * removed).
     */
    seguroEmpateSetInsured(selection, value) {
      const outcomeId = _.get(selection, 'outcome.id')

      if (value) {
        const eventId = _.get(selection, 'event.id')
        const insuredMap = _.get(this.$store.state.betslip, 'insured', {})
        _.forEach(_.get(this.$store.state.betslip, 'selections', []), (other) => {
          const otherId = _.get(other, 'outcome.id')
          if (otherId === outcomeId) return
          if (!eventId || _.get(other, 'event.id') !== eventId) return
          if (!_.get(insuredMap, otherId)) return
          this.$store.commit('betslip/setInsured', { key: otherId, value: false })
        })
      }

      this.$store.commit('betslip/setInsured', { key: outcomeId, value: !!value })
    },

    /**
     * Projected freebet for a stake, resolved against the campaign covering
     * this selection's event. Mirrors PromoCodeSetting::getAmount() in
     * 777es-bet-core so the betslip cannot promise a different figure than
     * settlement issues. Returns null when the prize is not configured, which
     * callers must render as "no amount" rather than zero.
     */
    seguroEmpateFreebetAmount(selection, stake) {
      const prize = this.seguroEmpatePrizeFor(_.get(selection, 'event'))
      const amount = parseFloat(_.get(prize, 'value', 0))
      const stakeValue = parseFloat(stake)
      if (!prize || !amount || !stakeValue || stakeValue <= 0) return null

      let freebet = 0
      const priceType = _.get(prize, 'price_type')
      if (priceType === 'fixed-price') freebet = amount
      if (priceType === 'percent') freebet = stakeValue * (amount / 100)
      if (priceType === 'same-stake') freebet = stakeValue
      if (!freebet) return null

      const cap = parseFloat(_.get(prize, 'max_value', 0))
      return cap && freebet > cap ? cap : freebet
    }
  }
}